import type {
  CardActivitySocketPayload,
  PayinSocketPayload,
  PayoutBatchSocketPayload,
  PayoutSocketPayload,
} from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { buildWalletOperationPath } from "@/modules/dashboard/wallets/utils/walletOperationLinks";
import type { BrebKeyIncomingPayment } from "@/shared/components/payments/breb-key/brebKeyIncomingPayments";
import type { DashboardAlertsTranslations } from "@/shared/lang/interfaces/dashboardAlerts";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import type { AlertBurst } from "./dashboardAlertBursts";
import {
  DASHBOARD_ALERT_CATALOG,
  type DashboardAlertGroup,
  type DashboardAlertType,
} from "./dashboardAlertCatalog";

/** One alert, ready to show: what happened, in words, and where to look at it. */
export interface DashboardAlert {
  type: DashboardAlertType;
  /** Unique per thing that happened within its type; with the type, what dedupes it across channels and tabs. */
  id: string;
  variant: "success" | "error" | "warning" | "default";
  title: string;
  description: string | null;
  /** The figure a burst summary adds up, with its currency. */
  amount: string | null;
  currency: string | null;
  action: { label: string; href: string };
}

export type DashboardAlertsT = (
  key: keyof DashboardAlertsTranslations,
  options?: Record<string, string | number>,
) => string;

export interface DashboardAlertTextContext {
  t: DashboardAlertsT;
  language: string;
}

/** The id an announcement is claimed and marked by: the same for the toast and for a screen that shows it. */
export function announcementIdFor(type: DashboardAlertType, id: string): string {
  return `${type}:${id}`;
}

const money = (amount: string, currency: string, language: string) =>
  `${formatCurrencyAmount(amount, currency, language)} ${currency}`;

const joined = (parts: readonly (string | null | undefined)[]) => parts.filter(Boolean).join(" · ") || null;

// "Ver transacción" opens the movement in the wallet the money moved, not the payments activity.
const payinMovementPath = (id: string) => buildWalletOperationPath("payins", id);
const payoutMovementPath = (id: string) => buildWalletOperationPath("payouts", id);

/**
 * A payment that landed on one of the organization's Bre-B keys, through its any-amount QR or the key typed
 * in.
 */
export function brebKeyPaymentAlert(
  payment: BrebKeyIncomingPayment,
  { t, language }: DashboardAlertTextContext,
): DashboardAlert {
  return {
    type: "payins_breb_key",
    id: payment.id,
    variant: "success",
    title: t("alert_payin_title", { amount: money(payment.grossAmount, "COP", language) }),
    description: joined([
      payment.payerName ? t("alert_payin_from", { name: payment.payerName }) : null,
      payment.payerBankName,
    ]),
    amount: payment.grossAmount,
    currency: "COP",
    action: { label: t("alert_view_transaction"), href: payinMovementPath(payment.id) },
  };
}

/** A collection the organization created that was just paid, on any rail. */
export function collectionPaidAlert(
  payin: Pick<PayinSocketPayload, "id" | "rail" | "currency" | "gross_amount" | "net_amount">,
  { t, language }: DashboardAlertTextContext,
): DashboardAlert | null {
  const amount = payin.gross_amount ?? payin.net_amount;
  if (!amount) return null;
  return {
    type: "payins_collections",
    id: payin.id,
    variant: "success",
    title: t("alert_payin_title", { amount: money(amount, payin.currency, language) }),
    description: t("alert_payin_collection_via", { rail: formatRail(payin.rail) }),
    amount,
    currency: payin.currency,
    action: { label: t("alert_view_transaction"), href: payinMovementPath(payin.id) },
  };
}

const PAYOUT_FAILED = new Set(["FAILED", "REVERSED"]);

/**
 * A payout that reached its destination, or that did not go through. Anything else (still moving, or
 * cancelled on purpose by someone) is not news.
 */
export function payoutAlert(
  payout: Pick<PayoutSocketPayload, "id" | "status" | "rail" | "currency" | "gross_amount" | "net_amount">,
  { t, language }: DashboardAlertTextContext,
): DashboardAlert | null {
  const paid = payout.status === "PAID";
  if (!paid && !PAYOUT_FAILED.has(payout.status)) return null;
  // What the beneficiary gets, which is what the person sending has in mind.
  const amount = payout.net_amount ?? payout.gross_amount;
  if (!amount) return null;
  const figure = money(amount, payout.currency, language);
  return {
    type: paid ? "payouts_paid" : "payouts_failed",
    id: `${payout.id}:${payout.status}`,
    variant: paid ? "success" : "error",
    title: t(paid ? "alert_payout_paid_title" : "alert_payout_failed_title", { amount: figure }),
    description: paid
      ? t("alert_payout_via", { rail: formatRail(payout.rail) })
      : t("alert_payout_failed_body"),
    amount,
    currency: payout.currency,
    action: { label: t("alert_view_transaction"), href: payoutMovementPath(payout.id) },
  };
}

const BATCH_OUTCOMES = {
  COMPLETED: { variant: "success", title: "alert_batch_completed_title" },
  COMPLETED_WITH_ERRORS: { variant: "warning", title: "alert_batch_completed_with_errors_title" },
  FAILED: { variant: "error", title: "alert_batch_failed_title" },
} as const satisfies Record<
  string,
  { variant: DashboardAlert["variant"]; title: keyof DashboardAlertsTranslations }
>;

/** A payout batch that finished running, well or not. Every intermediate push is progress, not news. */
export function payoutBatchAlert(
  batch: Pick<PayoutBatchSocketPayload, "id" | "status" | "counters">,
  { t }: DashboardAlertTextContext,
): DashboardAlert | null {
  const outcome = BATCH_OUTCOMES[batch.status as keyof typeof BATCH_OUTCOMES];
  if (!outcome) return null;
  const paid = batch.counters?.paid ?? 0;
  const failed = (batch.counters?.failed ?? 0) + (batch.counters?.reversed ?? 0);
  return {
    type: "payout_batches_done",
    id: `${batch.id}:${batch.status}`,
    variant: outcome.variant,
    title: t(outcome.title),
    description: joined([
      paid > 0 ? t(paid === 1 ? "alert_batch_paid_one" : "alert_batch_paid", { count: paid }) : null,
      failed > 0
        ? t(failed === 1 ? "alert_batch_failed_rows_one" : "alert_batch_failed_rows", { count: failed })
        : null,
    ]),
    amount: null,
    currency: null,
    action: { label: t("alert_view_batch"), href: `/app/payout-batches/${batch.id}` },
  };
}

const CARD_KINDS = {
  purchase_approved: {
    type: "cards_purchase_approved",
    variant: "default",
    title: "alert_card_approved_title",
    untitledAmount: "alert_card_approved_no_amount",
  },
  purchase_declined: {
    type: "cards_purchase_declined",
    variant: "error",
    title: "alert_card_declined_title",
    untitledAmount: "alert_card_declined_no_amount",
  },
  refund: {
    type: "cards_refund",
    variant: "success",
    title: "alert_card_refund_title",
    untitledAmount: "alert_card_refund_no_amount",
  },
  reversal: {
    type: "cards_refund",
    variant: "success",
    title: "alert_card_reversal_title",
    untitledAmount: "alert_card_reversal_no_amount",
  },
} as const satisfies Record<
  CardActivitySocketPayload["kind"],
  {
    type: DashboardAlertType;
    variant: DashboardAlert["variant"];
    title: keyof DashboardAlertsTranslations;
    untitledAmount: keyof DashboardAlertsTranslations;
  }
>;

/** A purchase, decline, refund or reversal on one of the organization's cards. */
export function cardActivityAlert(
  activity: CardActivitySocketPayload,
  { t, language }: DashboardAlertTextContext,
): DashboardAlert | null {
  const kind = CARD_KINDS[activity.kind];
  if (!kind) return null;
  // Said without an amount the backend could not read, rather than "NaN" or not at all.
  const amount = activity.amount && Number.isFinite(Number(activity.amount)) ? activity.amount : null;
  return {
    type: kind.type,
    id: activity.id,
    variant: kind.variant,
    title: amount
      ? t(kind.title, { amount: money(amount, activity.currency, language) })
      : t(kind.untitledAmount),
    description: joined([
      activity.merchant_name?.trim() || null,
      activity.card_last_4 ? t("alert_card_detail", { last4: activity.card_last_4 }) : null,
    ]),
    amount,
    currency: activity.currency,
    action: { label: t("alert_view_card"), href: `/app/cards/cards/${activity.card_id}?tab=transactions` },
  };
}

const GROUP_LIST_PATH: Record<DashboardAlertGroup, string> = {
  payins: "/app/payments-activity?direction=payins",
  payouts: "/app/payments-activity?direction=payouts",
  payout_batches: "/app/payout-batches",
  // The card list: purchases live on each card, the movements page is the card wallet's statement.
  cards: "/app/cards/cards",
};

const SUMMARY_TITLE: Record<DashboardAlertType, keyof DashboardAlertsTranslations> = {
  payins_breb_key: "summary_payins_breb_key",
  payins_collections: "summary_payins_collections",
  payouts_paid: "summary_payouts_paid",
  payouts_failed: "summary_payouts_failed",
  payout_batches_done: "summary_payout_batches_done",
  cards_purchase_approved: "summary_cards_purchase_approved",
  cards_purchase_declined: "summary_cards_purchase_declined",
  cards_refund: "summary_cards_refund",
};

/** The one toast that stands for a burst of alerts of one kind: how many, how much, and the list they are in. */
export function burstSummaryAlert(
  burst: AlertBurst,
  variant: DashboardAlert["variant"],
  { t, language }: DashboardAlertTextContext,
): DashboardAlert {
  return {
    type: burst.type,
    id: "summary",
    variant,
    title: t(SUMMARY_TITLE[burst.type], { count: burst.count }),
    description: burst.total
      ? t("summary_total", { amount: money(burst.total.amount, burst.total.currency, language) })
      : null,
    amount: burst.total?.amount ?? null,
    currency: burst.total?.currency ?? null,
    action: { label: t("summary_view"), href: GROUP_LIST_PATH[DASHBOARD_ALERT_CATALOG[burst.type].group] },
  };
}
